import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  increment,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  KeyResultCreateSchema,
  KeyResultUpdateSchema,
  calculateKeyResultProgress,
  type KeyResultCreate,
  type KeyResultUpdate,
} from '../models/keyResult.schemas';
import type { KeyResult } from '../models/keyResult.types';

/**
 * Classe de serviço para operações Firestore relacionadas a Key Results
 */
class KeyResultServiceClass {
  private collectionName = 'keyResults';

  /**
   * Cria um novo Key Result
   * @param data - Dados do KR a ser criado
   * @returns ID do KR criado
   * @throws Error se o objetivo não estiver em status válido
   */
  async create(data: KeyResultCreate): Promise<string> {
    // Validar com Zod schema
    const validated = KeyResultCreateSchema.parse(data);

    // Validar que o objetivo existe e buscar dados
    const objectiveDoc = await getDoc(doc(db, 'objectives', validated.objectiveId));
    if (!objectiveDoc.exists()) {
      throw new Error('okr/objective-not-found: Objetivo não encontrado');
    }

    const objectiveData = objectiveDoc.data();

    // Validar status do objetivo (deve ser 'draft' ou 'active')
    if (objectiveData.status !== 'draft' && objectiveData.status !== 'active') {
      throw new Error('okr/invalid-objective-status: Key Results só podem ser criados em objetivos com status "draft" ou "active"');
    }

    // Calcular progresso inicial
    const currentValue = validated.currentValue ?? validated.initialValue;
    const progress = calculateKeyResultProgress(
      validated.initialValue,
      currentValue,
      validated.targetValue
    );

    // Criar documento
    const docRef = await addDoc(collection(db, this.collectionName), {
      ...validated,
      currentValue,
      progress,
      lastCheckInDate: null,
      checkInCount: 0,
      completedAt: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      _metadata: {
        version: 1,
        lastModifiedBy: validated.responsibleId,
      },
    });

    // Atualizar contagem de KRs no objetivo
    await updateDoc(doc(db, 'objectives', validated.objectiveId), {
      keyResultCount: increment(1),
      updatedAt: serverTimestamp(),
    });

    return docRef.id;
  }

  /**
   * Atualiza um Key Result existente
   * @param id - ID do KR
   * @param data - Dados a serem atualizados
   */
  async update(id: string, data: Partial<KeyResultUpdate>): Promise<void> {
    // Validar com Zod schema
    const validated = KeyResultUpdateSchema.partial().parse({ ...data, id });

    // Buscar KR atual
    const krDoc = await getDoc(doc(db, this.collectionName, id));
    if (!krDoc.exists()) {
      throw new Error('okr/key-result-not-found: Key Result não encontrado');
    }

    const currentData = krDoc.data();

    // Preparar dados de atualização
    const updateData: Record<string, unknown> = {
      ...validated,
      updatedAt: serverTimestamp(),
      '_metadata.version': increment(1),
      '_metadata.lastModifiedBy': data._metadata?.lastModifiedBy || currentData.responsibleId,
    };

    // Recalcular progresso se valores mudaram
    if (
      validated.currentValue !== undefined ||
      validated.initialValue !== undefined ||
      validated.targetValue !== undefined
    ) {
      const initialValue = validated.initialValue ?? currentData.initialValue;
      const targetValue = validated.targetValue ?? currentData.targetValue;
      const currentValue = validated.currentValue ?? currentData.currentValue;

      updateData.progress = calculateKeyResultProgress(initialValue, currentValue, targetValue);
    }

    // Remover campos que não devem ser atualizados
    delete updateData.id;
    delete updateData._metadata;

    // Remover campos undefined (Firestore não aceita valores undefined)
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    });

    // Atualizar documento
    await updateDoc(doc(db, this.collectionName, id), updateData);
  }

  /**
   * Atualiza o progresso de um Key Result
   * @param id - ID do KR
   * @param currentValue - Novo valor atual
   */
  async updateProgress(id: string, currentValue: number): Promise<void> {
    // Buscar KR atual
    const krDoc = await getDoc(doc(db, this.collectionName, id));
    if (!krDoc.exists()) {
      throw new Error('okr/key-result-not-found: Key Result não encontrado');
    }

    const currentData = krDoc.data();

    // Calcular novo progresso
    const progress = calculateKeyResultProgress(
      currentData.initialValue,
      currentValue,
      currentData.targetValue
    );

    // Preparar dados de atualização
    const updateData: Record<string, unknown> = {
      currentValue,
      progress,
      lastCheckInDate: serverTimestamp(),
      checkInCount: increment(1),
      updatedAt: serverTimestamp(),
      '_metadata.version': increment(1),
      '_metadata.lastModifiedBy': currentData.responsibleId,
    };

    // Atualizar documento
    await updateDoc(doc(db, this.collectionName, id), updateData);
  }

  /**
   * Busca um Key Result por ID
   * @param id - ID do KR
   * @returns Key Result encontrado
   */
  async getById(id: string): Promise<KeyResult> {
    const krDoc = await getDoc(doc(db, this.collectionName, id));

    if (!krDoc.exists()) {
      throw new Error('okr/key-result-not-found: Key Result não encontrado');
    }

    return this.formatKeyResult(krDoc.id, krDoc.data());
  }

  /**
   * Busca todos os Key Results de um objetivo
   * @param objectiveId - ID do objetivo
   * @returns Lista de Key Results
   */
  async getByObjective(objectiveId: string): Promise<KeyResult[]> {
    const krsQuery = query(
      collection(db, this.collectionName),
      where('objectiveId', '==', objectiveId),
      orderBy('createdAt', 'asc')
    );

    const snapshot = await getDocs(krsQuery);
    return snapshot.docs.map((doc) => this.formatKeyResult(doc.id, doc.data()));
  }

  /**
   * Busca todos os Key Results de um ciclo
   * @param cycleId - ID do ciclo
   * @returns Array de Key Results
   */
  async getByCycle(cycleId: string): Promise<KeyResult[]> {
    const krsQuery = query(
      collection(db, this.collectionName),
      where('cycleId', '==', cycleId),
      orderBy('createdAt', 'asc')
    );

    const snapshot = await getDocs(krsQuery);
    return snapshot.docs.map((doc) => this.formatKeyResult(doc.id, doc.data()));
  }

  /**
   * Remove um Key Result
   * @param id - ID do KR a ser removido
   * @returns Dados do KR removido (para rollback)
   */
  async delete(id: string): Promise<{ objectiveId: string }> {
    // Buscar KR para pegar objectiveId antes de deletar
    const krDoc = await getDoc(doc(db, this.collectionName, id));
    if (!krDoc.exists()) {
      throw new Error('okr/key-result-not-found: Key Result não encontrado');
    }

    const krData = krDoc.data();
    const objectiveId = krData.objectiveId as string;

    // Deletar documento (soft delete - atualizar status)
    await updateDoc(doc(db, this.collectionName, id), {
      status: 'archived',
      updatedAt: serverTimestamp(),
      '_metadata.version': increment(1),
    });

    // Decrementar contagem de KRs no objetivo
    await updateDoc(doc(db, 'objectives', objectiveId), {
      keyResultCount: increment(-1),
      updatedAt: serverTimestamp(),
    });

    return { objectiveId };
  }

  /**
   * Calcula o progresso de um Key Result
   * @param initial - Valor inicial
   * @param target - Valor alvo
   * @param current - Valor atual
   * @returns Progresso percentual (0-100)
   */
  calculateProgress(initial: number, target: number, current: number): number {
    return calculateKeyResultProgress(initial, current, target);
  }

  /**
   * Valida que um objetivo tem pelo menos 2 Key Results
   * @param objectiveId - ID do objetivo
   * @throws Error se a contagem for menor que 2
   */
  async validateKRCount(objectiveId: string): Promise<void> {
    const krsQuery = query(
      collection(db, this.collectionName),
      where('objectiveId', '==', objectiveId)
    );

    const snapshot = await getDocs(krsQuery);
    const count = snapshot.size;

    if (count < 2) {
      throw new Error('okr/insufficient-key-results: Mínimo de 2 Key Results por objetivo');
    }

    // Removed maximum KR limit - users can add as many as needed
  }

  /**
   * Inscreve-se para atualizações em tempo real de Key Results
   * @param objectiveId - ID do objetivo
   * @param callback - Função de callback chamada quando há atualizações
   * @returns Função de cancelamento de inscrição
   */
  subscribeToKeyResults(
    objectiveId: string,
    callback: (krs: KeyResult[]) => void
  ): () => void {
    const krsQuery = query(
      collection(db, this.collectionName),
      where('objectiveId', '==', objectiveId),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(krsQuery, (snapshot) => {
      const krs = snapshot.docs.map((doc) => this.formatKeyResult(doc.id, doc.data()));
      callback(krs);
    });

    return unsubscribe;
  }

  /**
   * Formata dados do Firestore para o tipo KeyResult
   * @private
   */
  private formatKeyResult(id: string, data: unknown): KeyResult {
    const kr = data as Record<string, unknown>;

    return {
      id,
      title: kr.title as string,
      description: (kr.description as string) || undefined,
      objectiveId: kr.objectiveId as string,
      objectiveTitle: kr.objectiveTitle as string,
      cycleId: kr.cycleId as string,
      metricType: kr.metricType as 'number' | 'percentage' | 'currency' | 'boolean',
      unit: (kr.unit as string) || undefined,
      initialValue: kr.initialValue as number,
      targetValue: kr.targetValue as number,
      currentValue: kr.currentValue as number,
      progress: (kr.progress as number) || 0,
      status: (kr.status as 'on_track' | 'at_risk' | 'off_track' | 'completed') || 'on_track',
      responsibleId: kr.responsibleId as string,
      responsibleName: kr.responsibleName as string,
      responsibleEmail: kr.responsibleEmail as string,
      impediments: (kr.impediments as string) || undefined,
      notes: (kr.notes as string) || undefined,
      lastCheckInDate: kr.lastCheckInDate ? this.toTimestamp(kr.lastCheckInDate) : undefined,
      checkInCount: (kr.checkInCount as number) || 0,
      startDate: this.toTimestamp(kr.startDate),
      targetDate: this.toTimestamp(kr.targetDate),
      completedAt: kr.completedAt ? this.toTimestamp(kr.completedAt) : undefined,
      createdAt: this.toTimestamp(kr.createdAt),
      updatedAt: this.toTimestamp(kr.updatedAt),
      _metadata: {
        version: (kr._metadata as { version: number }).version || 1,
        lastModifiedBy: (kr._metadata as { lastModifiedBy: string }).lastModifiedBy || '',
      },
    } as KeyResult;
  }

  /**
   * Garante que o valor é um Timestamp do Firestore
   * @private
   */
  private toTimestamp(value: unknown): Timestamp {
    if (value instanceof Timestamp) {
      return value;
    }
    if (value instanceof Date) {
      return Timestamp.fromDate(value);
    }
    return Timestamp.now();
  }
}

export const keyResultService = new KeyResultServiceClass();
